import { TagModel } from "@/models/title-list-model";
import TitleListState from "@/modules/title-list";
import { ContentTextEditModel } from "@/services/content";
import { EmptyGuid } from "@/util/constants";
import { copyToClipboard, disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Tag, Input, Button, Icon, Select, Tooltip, Modal } from "ant-design-vue";
import { v4 } from "uuid";
import { fmtMsg, ActionItem, Action } from "commonui";
import { TitleListLocale } from "@/locales/localeid";
import { cloneDeep, debounce, groupBy } from "lodash";
import { Upload } from "ant-design-vue";
import * as GC from "@grapecity/spread-sheets";
import * as ExcelIO from "@grapecity/spread-excelio";
import { ExcelIOJsonModel } from "./interface";
import { AccessLevel } from "@/util/enum";
import { getEmptyTextLine } from "@/views/text-guardian/sub-views/title-list/sub-views/text/text-helper";
import { Paths } from "@/util/paths";

@Component({
	components: { Tag, Input, Button, Icon, Select, SelectOption: Select.Option, Tooltip, Upload, Action, ActionItem }
})
export default class TextTagEditor extends Vue {
	@Prop({ default: () => [] }) propTextDetails!: ContentTextEditModel[];
	@Prop() onTextChange!: () => void;
	@Prop() onLineRemoval!: () => void;
	@Prop() onTagRemoval!: () => void;
	@Prop() onTagAddition!: () => void;
	@Prop() onTextValidation!: (isValid: boolean) => void;
	@Prop({ default: false }) moveToBottom!: boolean; // move scrollbar to bottom when user adds new line at the end
	@Prop({ default: () => new Map<string, number>() }) textIdMap!: Map<string, number>; // since we are replacing integer values with guid for frontend proposes
	@Prop({ default: "ContentText" }) exportedExcelName!: string;

	private textDetails: ContentTextEditModel[] = [];
	public titleListModule = getModule(TitleListState);
	public tags: TagModel[] = this.titleListModule.tags;
	public visibleSelectNewIndex = -1;
	public activatedNewTagIndex = -1;
	public tagSearch: TagModel[] = [];
	public searching = false;
	public tagSeachWorker: Worker | undefined;
	public searchTerm: string = "";
	public selectedCodeId = null;
	public isAnyContentWithoutTag = false; // used for uniform UI (adds a class when any text don't have tag)
	public filesAccepted = ".xls,.xlsx";

	public fmtMsg = fmtMsg;
	public locale = TitleListLocale;
	private defaultExcelName = "ContentText";
	public accessLevel = AccessLevel;

	created() {
		this.tagSeachWorker = new Worker("@/util/workers/worker.ts", { type: "module" });

		const debouncedSearcher = debounce((event) => {
			this.tagSearch = event.data;
			this.searching = false;
		}, 300);

		this.tagSeachWorker.onmessage = debouncedSearcher;
	}

	beforeDestroy() {
		this.tagSeachWorker?.terminate();
	}

	@Watch("propTextDetails", { immediate: true, deep: true })
	updateListeners(details: ContentTextEditModel[] | null = null, validate = false) {
		const detailsToUpdate = details ? details : this.propTextDetails;
		// Update Text and Add listener
		this.textDetails = cloneDeep(detailsToUpdate);
		this.setHasTextDetails();
		this.addListener(this.textDetails.map((c) => c.id as string));
		this.isAnyContentWithoutTag = this.textDetails.some((d) => !d.code);
		if (validate) {
			Vue.nextTick(() => {
				this.validateText();
				// When the title/component is being saved for the first time propTextDetails has some property changes.
				// But since there are no changes in text check is applied here if there is some change in text or not.
				// If no changes in text then don't toggle the textpagedirty property in titlelist module.
				if (this.textDetails.some((text) => text.previousText !== text.textContent)) {
					this.onTextChange();
				}
			});
		}
	}

	get showCopy() {
		if (
			!this.textDetails.length ||
			(this.textDetails.length === 1 && !this.textDetails[0].textContent.length) ||
			!this.textDetails.some((s) => s.textContent.length)
		) {
			return false;
		}
		return true;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	get isNonCurriculumComponentPath() {
		return this.$route.path === Paths.NonCurriculumComponentsText ||this.$route.matched.slice(-1)[0].path === Paths.NonCurriculumComponentsEditText;
	}

	// For accessing through ref
	updatedTextDetails() {
		return this.textDetails.map((m, index) => {
			m.textContent = m.textContent.trim();
			m.sequence = index + 1;
			return m;
		});
	}

	handleSearch(value: string) {
		this.tagSearch = [];
		this.searchTerm = value.trim();
		if (value.length == 0) {
			this.searching ? (this.searching = false) : null;
			return;
		}
		this.searching = true;
		new Promise((res) => {
			this.tagSeachWorker?.postMessage({ tags: this.tags, value });
			res(true);
		});
	}

	handleChange(id: number, index: number) {
		const value = this.tagSearch.find((f) => f.id === id)?.code as string;
		this.textDetails[index].code = value;
		this.visibleSelectNewIndex = -1;
		this.activatedNewTagIndex = -1;
		this.tagSearch = [];
		this.validateText();
	}

	setIndex(index: number) {
		if (this.activatedNewTagIndex !== -1 || this.visibleSelectNewIndex === index) return;
		this.visibleSelectNewIndex = index;
	}

	addTagClicked(textId: string, index: number) {
		this.onTagAddition();
		this.textDetails[index].code = undefined;
		this.selectedCodeId = null;
		this.activatedNewTagIndex = index;
		this.isAnyContentWithoutTag = this.textDetails.some((d) => !d.code || !d.code.length);
	}

	getClass() {
		if (!this.isNonCurriculumComponentPath) {
			return "delete";
		}
		return "delete non-curr";
	}

	showRemoveLine() {
		if (this.textDetails.length == 1 && this.textDetails[0].textContent == "") {
			return false;
		}
		return true;
	}

	removeLine(textId: string) {
		this.onLineRemoval();
		if (this.textDetails.length == 1) {
			this.textDetails[0].textContent = "";
			this.textDetails[0].code = "";
		} else {
			const updatedTextDetails = this.textDetails.filter((object) => {
				return object.id != textId;
			});
			this.textDetails = updatedTextDetails;
		}
		this.activatedNewTagIndex = -1;
	}

	removeAllLines() {
		this.onLineRemoval();
		if (this.textDetails.length == 1) {
			this.textDetails[0].textContent = "";
			this.textDetails[0].code = "";
		} else {
			const updatedTextDetails = this.textDetails;
			updatedTextDetails[0].textContent = "";
			updatedTextDetails[0].code = "";
			this.textDetails = [];
			this.textDetails.push(updatedTextDetails[0]);
		}
		this.activatedNewTagIndex = -1;
	}

	hideSelectTag() {
		this.activatedNewTagIndex = -1;
	}

	onTagRemove(textId: string, index: number) {
		this.onTagRemoval();
		const conCopy = { ...this.textDetails[index] };
		conCopy.code = undefined;
		this.textDetails.splice(index, 1, conCopy);
		this.isAnyContentWithoutTag = true;
		this.validateText();
	}

	addListener(refs: string[], focusToo: boolean = false, timeout: number = 100) {
		Vue.nextTick(async function () {
			setTimeout(() => {
				refs.forEach((id, index) => {
					const input: HTMLInputElement = this.$refs[id][0].$el as HTMLInputElement;
					if (focusToo && refs.length === index + 1) {
						this.moveCaret(input, input.value.length);
					}
					input.addEventListener("keyup", (e) => this.backSpacePressed(e, id), false);
				}, timeout);
			});
		}, this);
	}

	moveCaret(input: HTMLInputElement & any, caretPosition: number) {
		if (input.setSelectionRange) {
			input.focus();
			input.setSelectionRange(caretPosition, caretPosition);
			// IE8 and below
		} else if (input.createTextRange) {
			const range = input.createTextRange();
			range.move("character", caretPosition);
			range.select();
		}
	}

	textChanged(e: InputEvent & { target: { value: string } }, textId: string, index: number) {
		this.onTextChange();
		// if last element have newline then create a new item and focus on that
		const text = e.target?.value as string;
		const newLineRegex = new RegExp(`(\r|\n|\r\n)`);
		let splits = text.split(newLineRegex);
		if (splits.length > 2) {
			this.textDetails[index].textContent = splits[0];
			splits = splits.filter((s, i) => s != "\n" && s != "\r\n" && s != "\r" && i != 0);
			const newLineIds = splits.map((e) => v4());

			// scroll the editor to the end, if user adds new line at the end
			const isAtTheEnd = this.textDetails.length === index + 1;
			if (this.moveToBottom && isAtTheEnd) {
				Vue.nextTick(() => {
					Vue.nextTick(() => {
						const el = document.getElementsByClassName("vc-layout__content")[0];
						el.scrollTo(0, el.scrollHeight);
					});
				});
			}
			this.addListener(newLineIds, true, 100);
			// add item with splitted data
			this.textDetails.splice(
				index + 1,
				0,
				...newLineIds.map((id, i) => ({
					id,
					contentId: this.titleListModule.editedContentId.length
						? this.titleListModule.editedContentId
						: EmptyGuid,
					textContent: splits[i],
					previousText: splits[i],
					revision: this.titleListModule.currentRevision,
					sequence: index + 1 + i
				}))
			);
		} else {
			// simply update the item
			this.textDetails[index].previousText = this.textDetails[index].textContent;
			this.textDetails[index].textContent = e.target.value;
		}
		this.validateText();
		this.setHasTextDetails();
	}

	backSpacePressed(e: KeyboardEvent & any, inputId: string) {
		const contentIndex = this.textDetails.findIndex((c) => c.id === inputId);
		const inputCurrentText = this.textDetails[contentIndex].previousText;
		const valueAfterEvent = e.target?.value;
		if (e.keyCode === 8) {
			this.onTextChange();
			//if content is same then user has pressed backspace at the start of input
			if (valueAfterEvent === inputCurrentText && contentIndex > 0) {
				Vue.nextTick(() => {
					// delete current line and append on parent
					const caretPos = this.textDetails[contentIndex - 1].textContent.length;
					this.textDetails.splice(contentIndex, 1);
					this.textDetails[contentIndex - 1].textContent = this.textDetails[
						contentIndex - 1
					].textContent.concat(inputCurrentText as string);
					const input: HTMLInputElement = this.$refs[this.textDetails[contentIndex - 1].id as string][0]
						.$el as HTMLInputElement;
					this.validateText();
					// focus just before concat after the rendering is complete
					Vue.nextTick(() => {
						this.moveCaret(input, caretPos);
					}, this);
				}, this);
			} else {
				this.textDetails[contentIndex].previousText = valueAfterEvent;
			}
		}
	}

	public copyText() {
		const textString = this.textDetails.map((text) => text.textContent);
		copyToClipboard(textString.join("\n"));
	}

	updateTagIndex() {
		this.activatedNewTagIndex = -1;
	}

	validateText() {
		// if (
		// 	!this.textDetails.filter((f) => f.textContent.trim().length).length &&
		// 	!this.textDetails.filter((f) => (f.code ? f.code.trim().length : false)).length
		// ) {
		// 	this.onTextValidation(true);
		// 	return;
		// }
		// if (this.textDetails.some((s) => s.textContent.trim().length === 0)) {
		// 	this.onTextValidation(false);
		// } else {
		// 	this.onTextValidation(true);
		// }
		this.onTextValidation(true);
	}

	/**
	 * Called when user clicks on export button on
	 * @function
	 */
	exportAsExcel() {
		// prepare details to export
		const exportDetails = this.textDetails.map((m) => {
			// find textId in map. if present then return else return empty string
			const fId = m.id as string;
			const iId = this.textIdMap.get(fId);
			if (m.textContent == "") {
				return ["", m.textContent, m.code];
			}
			if (iId) {
				return [iId, m.textContent, m.code];
			}
			return ["", m.textContent, m.code];
		});
		// column names
		const cols = ["Id", "Text", "Tag"];
		const data: (string | number | undefined)[][] = [cols, ...exportDetails];

		const contentName =
			this.exportedExcelName && this.exportedExcelName.length ? this.exportedExcelName : this.defaultExcelName;

		const contentRevision = this.titleListModule.currentRevision > -1 ? this.titleListModule.currentRevision : 0;
		const fileName =
			contentName + " (Revision - " + (contentRevision == -1 ? 0 : contentRevision.toString()) + ")" + ".xlsx";

		const workbook = new GC.Spread.Sheets.Workbook(undefined, {
			sheetCount: 0,
			font: "14px Arial"
		});
		const excelIO = new ExcelIO.IO();

		// According to excel limitations sheet names cannot contain ":".
		const sheetName = contentName.replace(/:/gm, "");
		const sheet = new GC.Spread.Sheets.Worksheet(sheetName);
		sheet.addColumns(0, 3);
		sheet.addRows(0, data.length);
		data.forEach((valueAarray, row) => {
			valueAarray.forEach((value, col) => {
				sheet.setValue(row, col, value);
			});
		});

		// styling and comment
		// color of header row
		sheet.getRange(0, 0, 1, cols.length, GC.Spread.Sheets.SheetArea.viewport).backColor("#d34c02");
		sheet.getRange(0, 0, 1, cols.length, GC.Spread.Sheets.SheetArea.viewport).foreColor("#ffffff");
		// size of header and then rest of columns
		sheet.getRange(0, 0, 1, cols.length, GC.Spread.Sheets.SheetArea.viewport).font("16pt Arial");
		sheet.getRange(1, 0, 500, cols.length, GC.Spread.Sheets.SheetArea.viewport).font("12pt Arial");

		// update width of first and second column
		sheet.setColumnWidth(0, 70.0);
		sheet.setColumnWidth(1, 500);

		// height of first and rest of rows
		sheet.getRange(0, -1, 1, -1, GC.Spread.Sheets.SheetArea.viewport).height(28);
		sheet.getRange(1, -1, 500, -1, GC.Spread.Sheets.SheetArea.viewport).height(24);

		// vertical align all rows
		sheet
			.getRange(1, -1, 500, -1, GC.Spread.Sheets.SheetArea.viewport)
			.vAlign(GC.Spread.Sheets.VerticalAlign.center);

		// left align first column
		sheet
			.getRange(0, 0, 500, 1, GC.Spread.Sheets.SheetArea.viewport)
			.hAlign(GC.Spread.Sheets.HorizontalAlign.center);

		// Add comment to first cell
		const comment = new GC.Spread.Sheets.Comments.Comment("Id could be left blank.");
		comment.backColor("#ffffe1");
		comment.borderColor("none");
		comment.zIndex(1);
		comment.location(new GC.Spread.Sheets.Point(8, -5));
		comment.width(120);
		comment.height(106);
		comment.locked(true);
		comment.lockText(true);
		sheet.getCell(0, 0).comment(comment);

		// add sheet to workbook on first position
		workbook.addSheet(0, sheet);

		const json = JSON.stringify(workbook.toJSON());
		excelIO.save(
			json,
			(blob) => {
				saveAs(blob, fileName);
				this.$message.success(fmtMsg(TitleListLocale.TitleListExportSuccess, { contentRevision, contentName }));
			},
			() => {
				this.$message.warning(fmtMsg(TitleListLocale.TitleListErrorOccurred));
			}
		);
	}

	/**
	 * Called when user imports the text
	 * @param file {File}
	 * @function
	 */
	beforeUpload(file: File) {
		const excelIO = new ExcelIO.IO();
		excelIO.open(
			file,
			async (json: ExcelIOJsonModel) => {
				// check number of sheets
				if (json.sheetCount == 0) {
					this.$message.warning(fmtMsg(TitleListLocale.TitleListNoSheet));
					return;
				}
				// if sheets are more than one then show message
				if (json.sheetCount == 0) {
					var shouldContinue = await new Promise((res, rej) => {
						Modal.confirm({
							title: fmtMsg(TitleListLocale.TitleListMoreThanOneSheet),
							onOk: () => res(true),
							onCancel: () => rej(false)
						});
					});
					if (!shouldContinue) {
						return;
					}
				}
				// sanitize data and update in view
				const records = this.sanitizeUploadedData(json);

				// if after sanitize data is null, format is incorrect
				if (records == null) {
					this.$message.warning(fmtMsg(TitleListLocale.TitleListInvalidData));
					return;
				}

				// if records have non-unique ids return
				const recordsToGroup = records.filter((f) => f.id && typeof f.id == "number");
				const group = groupBy(recordsToGroup, (e) => e?.id);
				if (group && Object.entries(group).some((s) => s[1].length > 1)) {
					this.$message.warning(fmtMsg(TitleListLocale.TitleListDuplicateId));
					return;
				}

				const tags = this.titleListModule.tags.map((m) => m.code);
				const invalidTags: string[] = [];
				// Update textDetails
				const newDetails = records.map((m, index) => {
					const lineId = m.id ? parseInt(m.id.toString().trim()) : 0;
					let newViewId = v4();
					// check if lineId is present in map, if yes, then assign that to newViewId
					for (const [key, value] of this.textIdMap) {
						if (value === lineId) {
							newViewId = key;
							break;
						}
					}
					const isValidTag = m.tag ? tags.includes(m.tag.trim()) : false;
					if (!isValidTag && m.tag) {
						invalidTags.push(newViewId);
					}
					return {
						id: newViewId,
						contentId: this.titleListModule.editedContentId.length
							? this.titleListModule.editedContentId
							: EmptyGuid,
						textContent: m.text.trim(),
						previousText: m.text.trim(),
						revision: this.titleListModule.currentRevision,
						sequence: index + 1,
						code: isValidTag ? m.tag?.trim() : undefined
					};
				});

				// insert an empty text line, if all the data is cleared
				if (newDetails.length === 0) {
					newDetails.push({
						...getEmptyTextLine(
							this.titleListModule.editedContentId.length
								? this.titleListModule.editedContentId
								: EmptyGuid
						),
						code: undefined
					});
					// text has changed, so call the callback
					if (
						this.textDetails.length > 1 ||
						(this.textDetails.length == 1 && this.textDetails[0].textContent.length)
					) {
						this.onTextChange();
					}
				}

				let textChanged = false;
				let tagChanged = false;
				// check if any tag is updated
				if (
					this.textDetails.some((s, index) => (newDetails[index] ? newDetails[index].code != s.code : false))
				) {
					this.onTagAddition();
					tagChanged = true;
				}

				// check for text change
				if (
					this.textDetails.length != newDetails.length ||
					this.textDetails.some((s, index) =>
						newDetails[index] ? newDetails[index].textContent !== s.textContent : false
					)
				) {
					this.onTextChange();
					textChanged = true;
				}

				if (!textChanged && !tagChanged) {
					if (invalidTags.length) {
						// Show warning and then success.
						// Give user the time to read warning. So using timeout.
						this.$message.warn(fmtMsg(TitleListLocale.TitleListInvalidTags, { count: invalidTags.length }));
						setTimeout(() => this.$message.warn(fmtMsg(TitleListLocale.TitelListImportNotChanged)), 3200);
					} else {
						this.$message.warn(fmtMsg(TitleListLocale.TitelListImportNotChanged));
					}
				} else {
					if (invalidTags.length) {
						// Show warning and then success.
						// Give user the time to read warning. So using timeout.
						this.$message.warn(fmtMsg(TitleListLocale.TitleListInvalidTags, { count: invalidTags.length }));
						setTimeout(() => this.$message.success(fmtMsg(TitleListLocale.TitelListImportSuccess)), 3200);
					} else {
						this.$message.success(fmtMsg(TitleListLocale.TitelListImportSuccess));
					}
				}

				this.updateListeners(newDetails, true);
			},
			() => {
				this.$message.warning(fmtMsg(TitleListLocale.TitleListErrorOccurred));
			}
		);
		return false;
	}

	/**
	 * Sanitizes the uploaded excel data
	 * @function
	 * @returns {{id?: number, text: string, tag?: string}[]}
	 */
	sanitizeUploadedData(json: ExcelIOJsonModel): { id?: number; text: string; tag?: string }[] | null {
		// get rows
		const sheetName = Object.keys(json.sheets)[0];
		const sheet = json.sheets[sheetName];
		const data = sheet.data.dataTable;
		if (
			Object.keys(data).length === 0 || // count of rows
			!data[0] || // first row (Header)
			Object.keys(data[0]).length !== 3 || // count of columns in first row
			!data[0][0] ||
			data[0][0].value === undefined || // value of first column in first row
			!data[0][1] ||
			data[0][1].value === undefined || // value of second column in first row
			!data[0][2] ||
			data[0][2].value === undefined // value of third column in first row
		) {
			return null;
		}
		// remove end rows without text value
		let rowValueFound = false;
		const filteredData = Object.keys(data)
			.reverse()
			.filter((key: string | number) => {
				// discard first row
				if (key == 0) {
					return false;
				}
				if (rowValueFound) {
					return true;
				}
				// If any of column have value, then keep an entry for all the rows including it and after that
				if (
					(data[key][0] && data[key][0].value !== undefined) ||
					(data[key][1] && data[key][1].value !== undefined) ||
					(data[key][2] && data[key][2].value !== undefined)
				) {
					rowValueFound = true;
					return true;
				}
				return false;
			})
			.reverse()
			.map((key: string | number) => {
				// format data
				return {
					id: data[key][0] && data[key][0].value !== undefined ? data[key][0].value : undefined,
					text: data[key][1] && data[key][1].value !== undefined ? data[key][1].value.toString().trim() : "",
					tag:
						data[key][2] && data[key][2].value !== undefined
							? data[key][2].value.toString().trim()
							: undefined
				};
			});
		return filteredData as { id?: number; text: string; tag?: string }[];
	}

	public selectControlContainer(element: HTMLDivElement) {
		return element.parentNode;
	}

	private setHasTextDetails() {
		let hasTextDetails = false;
		if (this.textDetails && this.textDetails.filter((a) => !!a.textContent.length).length) {
			hasTextDetails = true;
		}
		if (this.titleListModule.hasTextDetails !== hasTextDetails) {
			this.titleListModule.setHasTextDetails(hasTextDetails);
		}
	}
}
